/**
 * connectionService.ts — HTTP client for /connections endpoints.
 *
 * Wraps the backend Connection routes built in Block 4:
 *   - GET  /connections                    → list (my perspective)
 *   - GET  /connections/lookup?query=...   → look up potential vendor on Eaze
 *   - GET  /connections/:id                → detail
 *   - POST /connections                    → create (with Eaze or external vendor)
 *   - POST /connections/:id/accept         → non-initiator accepts pending
 *   - POST /connections/:id/suspend        → either side suspends
 *   - POST /connections/:id/reactivate     → reactivate suspended
 *   - PUT  /connections/:id                → update settings (autoPostOnClose, notes)
 *   - POST /connections/:id/close          → close (initiator only)
 */
import api from "./api";

// ─── Types (mirror backend Connection schema) ──────────────────────

export interface ConnectionSide {
  companyId: string;
  businessId: string;
  displayName: string;
  contactName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
}

export interface ConnectionStatusHistoryEntry {
  from?: string | null;
  to: string;
  reason?: string | null;
  actor?: string | null;
  actorRole?: string | null;
  at: string;
}

export interface Connection {
  _id: string;
  initiatedBy: "distributor" | "vendor";
  distributor: ConnectionSide;
  vendor: ConnectionSide;
  vendorOnEaze: boolean;
  externalVendorInfo?: {
    legalName?: string | null;
    taxId?: string | null;
    address?: string | null;
  } | null;
  autoPostOnClose: boolean;
  status: "pending" | "active" | "suspended" | "closed";
  statusHistory: ConnectionStatusHistoryEntry[];
  notes?: string | null;
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
  myRole: "distributor" | "vendor";
}

export interface VendorLookupResult {
  onEaze: boolean;
  companyId?: string;
  businessId?: string;
  businessName?: string;
}

export interface CreateConnectionInput {
  initiatorRole?: "distributor" | "vendor";
  vendorOnEaze: boolean;
  // When vendorOnEaze=true:
  otherSideBusinessId?: string;
  otherSideCompanyId?: string;
  otherContactName?: string;
  otherContactPhone?: string;
  otherContactEmail?: string;
  // When vendorOnEaze=false:
  externalVendorName?: string;
  externalVendorLegalName?: string;
  externalVendorTaxId?: string;
  externalVendorAddress?: string;
  // Common:
  ourContactPhone?: string;
  autoPostOnClose?: boolean;
  notes?: string;
}

export interface UpdateConnectionInput {
  autoPostOnClose?: boolean;
  notes?: string;
}

// ─── API surface ───────────────────────────────────────────────────

export const connectionService = {
  list: async (status?: string): Promise<Connection[]> => {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    const res = await api.get<{ connections: Connection[] }>("/connections", {
      params,
    });
    return res.data.connections;
  },

  lookupVendor: async (query: string): Promise<VendorLookupResult> => {
    const res = await api.get<VendorLookupResult>("/connections/lookup", {
      params: { query },
    });
    return res.data;
  },

  get: async (id: string): Promise<Connection> => {
    const res = await api.get<{ connection: Connection }>(`/connections/${id}`);
    return res.data.connection;
  },

  create: async (input: CreateConnectionInput): Promise<Connection> => {
    const res = await api.post<{ connection: Connection }>(
      "/connections",
      input,
    );
    return res.data.connection;
  },

  accept: async (id: string): Promise<Connection> => {
    const res = await api.post<{ connection: Connection }>(
      `/connections/${id}/accept`,
    );
    return res.data.connection;
  },

  suspend: async (id: string, reason?: string): Promise<Connection> => {
    const res = await api.post<{ connection: Connection }>(
      `/connections/${id}/suspend`,
      { reason },
    );
    return res.data.connection;
  },

  reactivate: async (id: string): Promise<Connection> => {
    const res = await api.post<{ connection: Connection }>(
      `/connections/${id}/reactivate`,
    );
    return res.data.connection;
  },

  update: async (
    id: string,
    input: UpdateConnectionInput,
  ): Promise<Connection> => {
    const res = await api.put<{ connection: Connection }>(
      `/connections/${id}`,
      input,
    );
    return res.data.connection;
  },

  close: async (id: string, reason?: string): Promise<Connection> => {
    const res = await api.post<{ connection: Connection }>(
      `/connections/${id}/close`,
      { reason },
    );
    return res.data.connection;
  },
};

export default connectionService;
